import { Box, Package, Scale } from "lucide-react";
import { useEffect, useState } from "react";

import { InputDecimal } from "@/components/ui/input-decimal";

import Secao from "./components/Secao";
import { compraDe, derivar, usoDe } from "./lib/unidade";

/** Cartao de opcao grande e igual aos irmaos: icone + nome + legenda. */
const Opcao = ({ ativo, onClick, disabled, testId, Icone, nome, legenda }) => (
  <button type="button" onClick={onClick} disabled={disabled} data-e2e={testId} aria-pressed={ativo}
    className={`flex flex-col items-center justify-center gap-1 rounded-xl border-2 px-2 py-3 transition-colors ${ativo ? "border-primary bg-primary/10 text-primary" : "border-border bg-background hover:border-primary/50"} disabled:opacity-40 disabled:cursor-not-allowed`}>
    <Icone className="w-5 h-5" />
    <span className="text-sm font-semibold leading-tight">{nome}</span>
    <span className={`text-[11px] leading-tight ${ativo ? "text-primary/80" : "text-muted-foreground"}`}>{legenda}</span>
  </button>
);

const fmt3 = (n) => n.toFixed(3).replace(".", ",");

/** Secoes "Como voce compra" e "Como voce usa na ficha" do item de estoque.
 *
 *  `value` = {unit, unitsPerPackage, unitWeight} (strings, como o form guarda)
 *  e `onChange(next)` devolve a mesma forma — o dado gravado nao mudou:
 *    compra kg  + usa un → unit kg, unit_weight X   (ficha conta em un)
 *    compra un  + usa kg → unit un, unit_weight X   (ficha usa gramatura)
 *    compra caixa       → unit un, units_per_package N (ficha em un)
 *
 *  As duas respostas sao ESCOLHA do usuario e vivem aqui: antes de digitar o N
 *  da caixa ou o peso, o dado gravado nao consegue representar "caixa" nem
 *  "compra kg, usa un". Quando o dado externo ja traz N ou peso (edicao), ele
 *  manda. O dialog desmonta ao fechar, entao nao sobra escolha entre itens. */
export default function SecoesUnidade({ value, onChange, numero = 2, idPrefix = "" }) {
  const peso = value.unitWeight || "";
  const pesoNum = Number(peso) || 0;
  const n = Number(value.unitsPerPackage) || 0;
  const [compra, setCompra] = useState(() => compraDe(value));
  const [uso, setUso] = useState(() => usoDe(value));
  useEffect(() => {
    const c = compraDe(value);
    setCompra((prev) => (c === "un" && prev === "caixa" && !(n > 0) ? "caixa" : c));
    setUso((prev) => {
      if (!c) return null;
      if (c === "caixa" || pesoNum > 0) return usoDe(value);
      if (prev === null) return c === "kg" ? "kg" : "un";
      return prev;
    });
  }, [value.unit, value.unitsPerPackage, pesoNum]); // eslint-disable-line react-hooks/exhaustive-deps
  const emitir = (c, u, extra = {}) => {
    setCompra(c);
    setUso(c === "caixa" ? "un" : u);
    onChange(derivar(c, u, { unitsPerPackage: value.unitsPerPackage, unitWeight: value.unitWeight, ...extra }));
  };
  const precisaPeso = (compra === "kg" && uso === "un") || (compra === "un" && uso === "kg");

  return (
    <>
      <Secao numero={numero} titulo="Como você compra este item?" testId="secao-compra">
        <div className="grid grid-cols-3 gap-2" data-e2e="unidade-compra-uso">
          <Opcao testId="compra-kg" Icone={Scale} nome="Peso" legenda="por kg" ativo={compra === "kg"} onClick={() => emitir("kg", "kg")} />
          <Opcao testId="compra-un" Icone={Box} nome="Unidade" legenda="por un" ativo={compra === "un"} onClick={() => emitir("un", "un")} />
          <Opcao testId="compra-caixa" Icone={Package} nome="Caixa / pacote" legenda="com N unidades" ativo={compra === "caixa"} onClick={() => emitir("caixa", "un")} />
        </div>
        {compra === "caixa" && (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span>Cada caixa tem</span>
            <div className="w-24"><InputDecimal id={`${idPrefix}unitsPerPackage`} decimals={0} sufixo="un" value={value.unitsPerPackage || ""} onChange={(v) => emitir("caixa", "un", { unitsPerPackage: v })} className="h-9" /></div>
            <span className="text-xs text-muted-foreground">— estoque e preço ficam por unidade</span>
          </div>
        )}
      </Secao>

      <Secao numero={numero + 1} titulo="Como você usa na ficha técnica?" testId="secao-uso"
        ajuda={compra === "caixa" ? "Item comprado em caixa é usado por unidade." : undefined}>
        <div className="grid grid-cols-2 gap-2">
          <Opcao testId="uso-kg" Icone={Scale} nome="Peso" legenda="em kg (gramatura)" ativo={uso === "kg"} disabled={compra === "caixa"} onClick={() => emitir(compra, "kg")} />
          <Opcao testId="uso-un" Icone={Box} nome="Unidade" legenda="em un" ativo={uso === "un"} onClick={() => emitir(compra, "un")} />
        </div>
        {precisaPeso && (
          <div className="rounded-lg border bg-muted/30 p-3 space-y-1.5">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span>1 unidade pesa</span>
              <div className="w-28"><InputDecimal id={`${idPrefix}unitWeight`} decimals={3} sufixo="kg" value={peso} onChange={(v) => emitir(compra, uso, { unitWeight: v })} className="h-9" /></div>
            </div>
            <p className="text-xs text-muted-foreground" data-e2e="dica-peso">
              {pesoNum > 0
                ? (compra === "kg"
                  ? <>1 kg rende <strong className="text-foreground">{(1 / pesoNum).toFixed(2).replace(".", ",")} un</strong>; cada un vendida baixa {fmt3(pesoNum)} kg do estoque.</>
                  : <>0,100 kg na ficha baixa <strong className="text-foreground">{fmt3(0.1 / pesoNum)} un</strong> do estoque.</>)
                : <span className="text-amber-600 dark:text-amber-400" data-e2e="falta-peso">Informe o peso — sem ele a ficha fica na unidade da compra.</span>}
            </p>
          </div>
        )}
      </Secao>
    </>
  );
}
